/**
 * Predictive Search — search-as-you-type dropdown
 * Custom element <predictive-search>
 */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"], input[name="q"]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.debounceTimer = null;
    this.abortController = null;
    this.selectedIndex = -1;

    if (this.input && this.resultsContainer) {
      this.bindEvents();
    }
  }

  bindEvents() {
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length >= 2) {
        this.search(this.input.value.trim());
      }
    });

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeResults();
      }
    });
  }

  onInput() {
    clearTimeout(this.debounceTimer);
    const query = this.input.value.trim();

    if (query.length < 2) {
      this.closeResults();
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.search(query);
    }, 300);
  }

  onKeydown(e) {
    if (!this.resultsContainer.classList.contains('is-open')) return;

    const items = this.resultsContainer.querySelectorAll('.predictive-search__item');
    if (!items.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
        this.highlightItem(items);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.highlightItem(items);
        break;
      case 'Enter':
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          e.preventDefault();
          items[this.selectedIndex].click();
        }
        break;
      case 'Escape':
        this.closeResults();
        this.input.blur();
        break;
    }
  }

  highlightItem(items) {
    items.forEach((item, i) => {
      item.setAttribute('aria-selected', i === this.selectedIndex ? 'true' : 'false');
    });
  }

  async search(query) {
    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,article,page&resources[limit]=4`;
      const res = await fetch(url, {
        signal: this.abortController.signal,
        headers: { 'Accept': 'application/json' }
      });
      const data = await res.json();
      this.renderResults(data, query);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Predictive search error:', err);
      }
    }
  }

  renderResults(data, query) {
    const resources = data.resources?.results;
    if (!resources) {
      this.closeResults();
      return;
    }

    const products = resources.products || [];
    const articles = resources.articles || [];
    const pages = resources.pages || [];

    if (products.length === 0 && articles.length === 0 && pages.length === 0) {
      this.resultsContainer.innerHTML = `
        <div style="padding: 16px; font-size: 13px; color: var(--color-fg-muted);">
          No results for "${this.escapeHtml(query)}"
        </div>
      `;
      this.openResults();
      return;
    }

    const esc = this.escapeHtml.bind(this);
    let html = '';

    if (products.length > 0) {
      html += `<p class="predictive-search__heading">Products</p>`;
      html += products.map(product => {
        const vendor = product.vendor || '';
        const priceLabel = this.formatMoney(product.price);
        const safeUrl = esc(product.url);
        const safeTitle = esc(product.title);
        const safeImg = product.image ? esc(product.image) : '';
        return `
        <a href="${safeUrl}" class="predictive-search__item">
          ${safeImg ? `<img src="${safeImg}" alt="${safeTitle}" loading="lazy">` : '<div class="predictive-search__item-placeholder"></div>'}
          <div class="predictive-search__item-info">
            ${vendor ? `<p class="predictive-search__item-vendor">${esc(vendor)}</p>` : ''}
            <p class="predictive-search__item-title">${safeTitle}</p>
            <p class="predictive-search__item-price">${esc(priceLabel)}</p>
          </div>
          <svg class="predictive-search__item-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </a>`;
      }).join('');
    }

    if (articles.length > 0) {
      html += `<p class="predictive-search__heading">Articles</p>`;
      html += articles.map(article => `
        <a href="${esc(article.url)}" class="predictive-search__item">
          <div class="predictive-search__item-info">
            <p class="predictive-search__item-title">${esc(article.title)}</p>
            <p class="predictive-search__item-type">Article</p>
          </div>
        </a>
      `).join('');
    }

    if (pages.length > 0) {
      html += `<p class="predictive-search__heading">Pages</p>`;
      html += pages.map(page => `
        <a href="${esc(page.url)}" class="predictive-search__item">
          <div class="predictive-search__item-info">
            <p class="predictive-search__item-title">${esc(page.title)}</p>
            <p class="predictive-search__item-type">Page</p>
          </div>
        </a>
      `).join('');
    }

    // Add "View all results" link
    html += `
      <a href="/search?q=${encodeURIComponent(query)}" class="predictive-search__item" style="justify-content: center; padding: 14px; font-size: 13px; font-weight: 500; color: var(--color-accent);">
        View all results
      </a>
    `;

    this.resultsContainer.innerHTML = html;
    this.selectedIndex = -1;
    this.openResults();
  }

  openResults() {
    this.resultsContainer.classList.add('is-open');
  }

  closeResults() {
    this.resultsContainer.classList.remove('is-open');
    this.selectedIndex = -1;
  }

  formatMoney(priceStr) {
    // Shopify suggest API returns price as a string like "199.00" or as cents
    const price = parseFloat(priceStr);
    if (isNaN(price)) return priceStr;
    // If price looks like cents (> 999), divide by 100; display without € or decimals
    const whole = price > 999 ? Math.round(price / 100) : Math.round(price);
    return `${whole},-`;
  }

  escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

customElements.define('predictive-search', PredictiveSearch);
